import {
  Button,
  Col,
  Form,
  FormProps,
  Input,
  List,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Typography,
  useTable,
} from '@pankod/refine-antd'
import { CrudFilters, HttpError, useList, usePermissions } from '@pankod/refine-core'
import locale from 'antd/lib/date-picker/locale/ja_JP'
import { ErrorComponent } from 'components/404/ErrorComponent'
import { Conditions } from 'components/download'
import { DateTimePicker } from 'components/parts/DateTimePicker'
import dayjs, { Dayjs } from 'dayjs'
import { ICampaign, ICampaignFilterVariables, ISponsor } from 'interfaces'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { getFilterToDateForDownload, validatePasteValueLength } from 'utils'
import './tableStyles.css'

const { Title, Text } = Typography
const { RangePicker } = DateTimePicker

export const CampaignDownload: React.FC = () => {
  const [rangeDateInput, setRangeDateInput] = useState<[Dayjs, Dayjs]>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 200

  const {
    tableProps,
    searchFormProps,
    tableQueryResult: campaignQueryResult,
  } = useTable<ICampaign, HttpError, ICampaignFilterVariables>({
    resource: 'campaigns',
    initialSorter: [
      {
        field: 'campaignId',
        order: 'asc',
      },
    ],
    permanentFilter: [
      {
        field: 'campaign.isActive',
        operator: 'eq',
        value: 'true',
      },
      {
        field: 'campaign.confirmationStatus',
        operator: 'eq',
        value: '4',
      },
      {
        field: 'campaign.isIndexDisplayable',
        operator: 'eq',
        value: 'true',
      },
    ],
    metaData: {
      operation: 'downloadCampaigns',
      current: currentPage,
      pageSize: pageSize,
      fields: [
        {
          basicInfo: [
            'id',
            'campaignId',
            'campaignName',
            'campaignFrom',
            'campaignTo',
            'confirmationStatus',
            'isActive',
            {
              client: ['id', 'clientName'],
            },
            {
              sponsor: ['id', 'sponsorName'],
            },
          ],
          tvCmInfo: [
            'cost',
            {
              cmMaterials: ['cmMaterialEditName'],
            },
          ],
          digitalPlacementInfo: ['cost'],
        },
      ],
    },
    onSearch: (params) => {
      const filters: CrudFilters = []
      const { sponsor, productName } = params

      filters.push({
        field: 'campaign.campaignFrom',
        operator: 'gte',
        value: rangeDateInput ? rangeDateInput[0]?.startOf('d').format('YYYY-MM-DDTHH:mm:ss') : null,
      })

      filters.push({
        field: 'campaign.campaignTo',
        operator: 'lte',
        value: rangeDateInput
          ? getFilterToDateForDownload(rangeDateInput[1]?.startOf('d'))
          : getFilterToDateForDownload(),
      })

      filters.push({
        field: 'sponsor.sponsorId',
        operator: 'eq',
        value: sponsor ?? null,
      })

      filters.push({
        field: 'campaign.productName',
        operator: 'contains',
        value: productName === '' ? null : productName,
      })

      return filters
    },
  })

  const { data: permissions } = usePermissions()
  const total = campaignQueryResult.data?.total ?? 0

  if (!permissions?.includes('CAMPAIGN_DOWNLOAD')) {
    return <ErrorComponent />
  }

  return (
    <>
      <Helmet>
        <title>{`ダウンロード | ${process.env.REACT_APP_SERVICE_NAME}`}</title>
        <meta name="description" content={`ダウンロード | ${process.env.REACT_APP_SERVICE_NAME}`} />
      </Helmet>
      <Space direction="vertical" style={{ display: 'flex' }}>
        <Title level={4}>ダウンロード</Title>

        <List title={false}>
          <Title level={5}>キャンペーン選択</Title>
          <CampaignFilter
            formProps={searchFormProps}
            dateInput={{ rangeDateInput: rangeDateInput, setRangeDateInput: setRangeDateInput }}
            length={total}
            setCurrentPage={setCurrentPage}
          />

          <Space direction="vertical" style={{ display: 'flex' }}>
            <Table
              {...tableProps}
              bordered
              size="small"
              scroll={{ x: true, y: '60vh' }}
              pagination={false}
              className="download-table"
              rowKey={(row: any) => row.basicInfo.id}
              rowSelection={{
                onChange: (selectedRowKeys: React.Key[]) => setSelectedRowKeys(selectedRowKeys),
              }}
            >
              <Table.Column dataIndex={['basicInfo', 'campaignName']} title="キャンペーン名" ellipsis />
              <Table.Column
                dataIndex={['basicInfo', 'campaignFrom']}
                title="開始日"
                ellipsis
                render={(_, record: ICampaign) => {
                  return record?.basicInfo?.campaignFrom
                    ? dayjs(record?.basicInfo?.campaignFrom).format('YYYY/MM/DD')
                    : ''
                }}
              />
              <Table.Column
                dataIndex={['basicInfo', 'campaignTo']}
                title="終了日"
                ellipsis
                render={(_, record: ICampaign) => {
                  return record?.basicInfo?.campaignTo ? dayjs(record?.basicInfo?.campaignTo).format('YYYY/MM/DD') : ''
                }}
              />
              <Table.Column
                dataIndex={['basicInfo', 'campaignFrom']}
                title="費用"
                ellipsis
                align="right"
                render={(_, record: ICampaign) => {
                  const tvCost = record?.tvCmInfo?.cost ? parseInt(record?.tvCmInfo?.cost) : 0
                  const digitalCost = record?.digitalPlacementInfo?.cost
                    ? parseInt(record?.digitalPlacementInfo?.cost)
                    : 0
                  const totalCost = tvCost + digitalCost
                  return `￥${totalCost.toLocaleString()}`
                }}
              />
              <Table.Column
                dataIndex={['basicInfo', 'sponsor', 'sponsorName']}
                title="TV素材名"
                ellipsis
                render={(_, record: ICampaign) => {
                  const materialNames = (record.tvCmInfo?.cmMaterials ?? [])
                    .map((cmMaterial) => cmMaterial.cmMaterialEditName)
                    .sort()
                    .join(', ')
                  return materialNames
                }}
              />
            </Table>
            <div style={{ textAlign: 'right', marginTop: '10px' }}>
              <Pagination
                current={currentPage}
                onChange={(page: number) => {
                  setCurrentPage(page)
                }}
                total={total}
                pageSize={pageSize}
                showSizeChanger={false}
              />
            </div>
          </Space>
        </List>
        <Conditions selectedCampaignIds={selectedRowKeys as string[]} />
      </Space>
    </>
  )
}

const CampaignFilter: React.FC<{
  formProps: FormProps
  dateInput: { rangeDateInput: [Dayjs, Dayjs] | undefined; setRangeDateInput: (d: [Dayjs, Dayjs]) => void }
  length: number
  setCurrentPage: (currentPage: number) => void
}> = (props) => {
  const { formProps, dateInput, length, setCurrentPage } = props
  const { rangeDateInput, setRangeDateInput } = dateInput
  const [sponsorCurrent, setSponsorCurrent] = useState(1)
  const [sponsorSearchInput, setSponsorSearchInput] = useState('')
  const [sponsorKeyword, setSponsorKeyword] = useState('')
  const [sponsorOptions, setSponsorOptions] = useState<{ label: string; value: string }[]>([])
  const sponsorPageSize = 50
  const sponsorFilters: CrudFilters = sponsorKeyword
    ? [
        {
          field: 'sponsorName',
          operator: 'contains',
          value: sponsorKeyword,
        },
      ]
    : []

  const sponsorQueryResult = useList<ISponsor, HttpError>({
    resource: 'sponsors',
    config: {
      pagination: {
        current: sponsorCurrent,
        pageSize: sponsorPageSize,
      },
      filters: sponsorFilters,
    },
    metaData: {
      fields: ['id', 'sponsorName'],
    },
    queryOptions: {
      keepPreviousData: true,
      onSuccess: (resp) => {
        const nextOptions = (resp.data ?? []).map((sponsor) => ({
          label: sponsor.sponsorName,
          value: sponsor.id,
        }))
        if (sponsorCurrent === 1) {
          setSponsorOptions(nextOptions)
          return
        }
        setSponsorOptions((prev) => {
          const merged = [...prev, ...nextOptions]
          const unique = new Map(merged.map((opt) => [opt.value, opt]))
          return Array.from(unique.values())
        })
      },
    },
  })

  const sponsorTotal = sponsorQueryResult.data?.total ?? 0
  const canLoadMoreSponsors = sponsorOptions.length < sponsorTotal

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSponsorKeyword(sponsorSearchInput.trim())
      setSponsorCurrent(1)
    }, 300)

    return () => {
      window.clearTimeout(timer)
    }
  }, [sponsorSearchInput])

  return (
    <>
      <Form
        {...formProps}
        size="small"
        onFinish={(values) => {
          setCurrentPage(1)
          return formProps.onFinish?.(values)
        }}
      >
        <Row gutter={[16, 8]}>
          <Col span={6}>
            <div
              data-testId={'test-filterPaste'}
              onPaste={(event: React.ClipboardEvent) => validatePasteValueLength(event, 100)}
            >
              <Text strong={true}>広告主</Text>
              <Form.Item name="sponsor" colon={false} style={{ marginTop: '2px', marginBottom: '14px' }}>
                <Select
                  options={sponsorOptions}
                  showArrow
                  showSearch
                  allowClear
                  onSearch={(value: string) => {
                    setSponsorSearchInput(value ?? '')
                  }}
                  onPopupScroll={(event: React.UIEvent<HTMLDivElement>) => {
                    const target = event.currentTarget
                    if (sponsorQueryResult.isFetching || !canLoadMoreSponsors) return
                    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 8) {
                      setSponsorCurrent((prev) => prev + 1)
                    }
                  }}
                  loading={sponsorQueryResult.isFetching}
                  filterOption={false}
                  mode="multiple"
                  placeholder={'部分一致/選択'}
                ></Select>
              </Form.Item>
            </div>
          </Col>
          <Col span={6}>
            <Text strong={true}>商品名</Text>
            <Form.Item name="productName" colon={false} style={{ marginTop: '2px', marginBottom: '14px' }}>
              <Input maxLength={100} placeholder={'部分一致'} />
            </Form.Item>
          </Col>
          <Col span={7}>
            <div
              data-testId={'test-filterPaste'}
              onPaste={(event: React.ClipboardEvent) => validatePasteValueLength(event, 30)}
            >
              <Text strong={true}>期間</Text>
              <Form.Item name="campaignFromTo" colon={false} style={{ marginTop: '2px', marginBottom: '14px' }}>
                <RangePicker
                  locale={locale}
                  style={{ width: '100%' }}
                  format="YYYY/MM/DD"
                  allowClear
                  allowEmpty={[true, true]}
                  placeholder={['開始日付', '終了日付']}
                  value={rangeDateInput}
                  onChange={(value: any) => {
                    setRangeDateInput(value)
                  }}
                  size="small"
                />
              </Form.Item>
            </div>
          </Col>
          <Col span={5}>
            <Space align="baseline">
              <Form.Item style={{ marginTop: '23px', marginBottom: '16px' }}>
                <Button htmlType="submit" type="primary">
                  {'絞り込み'}
                </Button>
              </Form.Item>
              <span>検索結果 {length} 件</span>
            </Space>
          </Col>
        </Row>
      </Form>
    </>
  )
}
